import { flatten, groupBy, intersection } from "lodash";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { widgetFilterTypes, widgetFilterTypesMapper } from "./types";
import { DatapointsEntityType } from "api/ux/src";
import { useCurrentDashboardOptions } from "components/workspaces/hooks/use-current-dashboard";
import { tenantSourcesSelector } from "hooks/use-tenant-settings";
import { useDisabledFilters, useSearchParam } from "store/template/widgets/add-widget/selectors";
import { getWidgetIndexAsDefaultWidgets } from "store/template/widgets/widget-index";
export const generateName = (widget) => {
    let actualName = widget.name ?? "Grouped Widget";
    const selectedValues = widget.componentProps?.dataSelectProps?.map((chartSelectSlotProps) => {
        let selectValues = {};
        Object.entries(chartSelectSlotProps.selectValues).forEach(([key, elem], index) => {
            if (elem.defaultValue || index === 0) {
                selectValues = { [key]: elem };
            }
        });
        return { ...chartSelectSlotProps, selectValues };
    });
    selectedValues && Object.entries(selectedValues).forEach(([num, value]) => {
        actualName = actualName?.replace(`$${num}`, `[${Object.values(value.selectValues)?.[0]?.displayName}]`);
    });
    return actualName;
};
const validateName = (widget, name) => (widget.name && name ? widget.name.toLowerCase().indexOf(name.toLowerCase()) > -1 : true);
const validateGenericTags = (widget, filter, selector) => !selector(filter) ||
    selector(filter)?.length === 0 ||
    intersection(selector(widget.metaData), selector(filter)).length > 0;
const validateEntityType = (widget, filter) => validateGenericTags(widget, filter, (w) => {
    if (w?.entityType?.some((e) => e === DatapointsEntityType.Asset)) {
        return w.entityType.concat(["Asset"]);
    }
    return w?.entityType;
});
const validateTags = (widget, filter) => validateGenericTags(widget, filter, (w) => w?.tags);
const validateInvestmentType = (widget, filter) => validateGenericTags(widget, filter, (w) => w?.investmentType);
const validateSource = (widget, filter) => validateGenericTags(widget, filter, (w) => w?.sources);
const validateWidgetType = (widget, filter) => validateGenericTags(widget, filter, (w) => w?.widgetType);
// The following values are commented temporarily while waiting for the ADS patch
const stringArrayToFacet = (array, type, searchFilter, disabledFilters) => Object.entries(groupBy(array, (x) => x))
    .map(([key], index) => ({
    checked: searchFilter?.[type]?.includes(key),
    disabled: disabledFilters?.includes(type),
    eventData: type,
    // FacetCount: value.length,
    label: key,
    // SecondaryLabel: value.length,
    uid: `${key}.${index}`
}));
const getActualWidget = (widget) => {
    if (widget.name || widget.componentProps?.widgets) {
        return { ...widget, name: generateName(widget) };
    }
    return null;
};
const myPositionOnVisibilityConditions = ["all", "myPositionsOnly"];
const myPositionOffVisibilityConditions = ["all", "wholeFundOnly"];
const filterCondition = (value) => {
    const metadata = value[1]?.metadata;
    if (metadata?.isDefault === false) {
        return false;
    }
    return value[1]?.metaData?.isDefault ?? true;
};
const useCurrentDashboardEligibleWidgets = () => {
    const fundDashboardOptions = useCurrentDashboardOptions();
    const allWidgetsFromIndex = useMemo(() => Object.entries(getWidgetIndexAsDefaultWidgets()), []);
    const addableWidgets = allWidgetsFromIndex.filter(filterCondition);
    if (fundDashboardOptions?.enabled) {
        return addableWidgets.filter((w) => w[1]?.metaData?.visibilityConditions
            ?.some((c) => myPositionOnVisibilityConditions.includes(c)));
    }
    return addableWidgets.filter((w) => w[1]?.metaData?.visibilityConditions
        ?.some((c) => myPositionOffVisibilityConditions.includes(c)) ||
        w[1]?.metaData?.visibilityConditions === undefined ||
        w[1]?.metaData?.visibilityConditions?.length === 0);
};
export const useAvailableWidgets = (filter, name) => {
    const widgets = useCurrentDashboardEligibleWidgets();
    return widgets.reduce((prev, [key, widget]) => {
        const actualWidget = getActualWidget(widget);
        if (actualWidget &&
            validateName(actualWidget, name) &&
            validateEntityType(actualWidget, filter) &&
            validateTags(actualWidget, filter) &&
            validateInvestmentType(actualWidget, filter) &&
            validateSource(actualWidget, filter) &&
            validateWidgetType(actualWidget, filter)) {
            prev[key] = { ...actualWidget, name: generateName(widget) };
        }
        return prev;
    }, {});
};
export const useAvailableFilters = (entityType) => {
    const modules = useSelector(tenantSourcesSelector);
    const allWidgets = useAvailableWidgets({ entityType: [entityType] }), disabledFilters = useDisabledFilters(), metaDatas = Object.values(allWidgets).map((widget) => widget.metaData), searchFilters = useSearchParam();
    const filters = {};
    widgetFilterTypesMapper.forEach((value, key) => {
        const filterOptions = stringArrayToFacet(flatten(metaDatas.map((m) => m[key]).filter((t) => t))
            .filter((t) => {
            if (value.toLowerCase() === widgetFilterTypes.sources) {
                return modules.includes(t);
            }
            return t;
        }), key, searchFilters, disabledFilters);
        if (filterOptions.length > 1 && value) {
            filters[value] = filterOptions;
        }
    });
    return filters;
};
